import { useCallback, useMemo, type ComponentProps, type ReactNode } from "react";
import {
  Box,
  EntitySelector,
  EntitySelectorContent,
  EntitySelectorEmpty,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorTrigger,
  Menu,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
  Separator,
  Text,
  type EntityBadgeConfig,
  type EntityItemConfig,
  type EntityPoolConfig,
  type EntitySelection,
  type EntitySelectorConfig,
  type OnChangeFn,
  type Omittable,
  type SortingState,
  useIsMobile,
} from "@salestrak/ui";

import { EntityTag } from "#app/components/entity";
import {
  TEAM_POOL_ID,
  teamToPoolItem,
  useTeamOpenBadge,
} from "#app/components/entity-selectors/hooks/use-team-pool";
import {
  USER_POOL_ID,
  useUserPool,
} from "#app/components/entity-selectors/hooks/use-user-pool";
import { usePoolSelection } from "#app/components/entity-selectors/use-pool-selection";
import { FilterToggle } from "#app/components/filters/filter-toggle";
import { FilterValueChip } from "#app/components/filters/filter-value-chip";
import {
  MemberFilterSheet,
  type MemberFacet,
  type MemberFacetOption,
} from "#app/components/members/member-filter-sheet";
import type { PresenceStatus, RoleRow, TeamOption, UserFacet } from "#app/lib/data";
import { useMemberEntityResolver } from "#app/lib/data/member-directory-provider";
import { useMemberFacetCounts } from "#app/lib/hooks/use-member-facet-counts";
import type { AppFilterDef, UseTableFiltersResult } from "#app/lib/hooks/use-table-filters";
import { combineWhereConditions } from "#app/lib/query-utils";
import { SortPresetChip, type SortPreset } from "#app/components/filters/sort-preset-chip";
import { PRESENCE_META, PRESENCE_ORDER } from "#app/lib/constants/presence-meta";
import { AnalyticsContext, MemberEvent, trackEvent } from "#app/lib/analytics";

/**
 * The member lists' quick filters: one labeled chip per facet — Status, Team,
 * Role, Reports to — each opening a single-select list headed by "All", plus
 * the Sort chip over the same five orderings every member list offers. The
 * chips read `Label ⌄` at rest and `Label: Value ⌄` once set, the value being
 * the option's label or, for an entity, its visual alone
 * (`FilterValueChip`).
 *
 * Every chip writes ONE value under its filter key into the shared
 * `useTableFilters` state, so the members table's group-by (which hides the
 * buckets a status or team filter excludes) keeps working off the same
 * filters the funnel used to hold. Status is the one chip over TWO keys:
 * membership (`memberStatus`) and live presence (`presence`) are different
 * facts on the server, and keeping them apart is what lets grouping by
 * status strip only the membership predicate from its per-bucket queries
 * while an "Online" pick still narrows every bucket.
 *
 * Every option carries the count of members picking it would show: the
 * server's `userFacetCounts` over the view's own scope (`scopeWhere` — the
 * team, the search) plus every OTHER chip's filter, so the numbers move with
 * the rest of the toolbar and each list's "All" states the view's total.
 *
 * On a phone the facet chips fold into ONE filter door and a bottom sheet
 * (`MemberFilterSheet`), and the Group and Sort chips hold their glyph alone:
 * the toolbar band reads as one line there, never two.
 */

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

export const MEMBER_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "invited", label: "Invited" },
  { value: "suspended", label: "Suspended" },
  // Members who removed themself. Their rows stay for the work that names
  // them; the bucket is where a manager finds who has gone.
  { value: "left", label: "Left" },
];

// The server knows six statuses; expired and declined invites fold into
// "Invited" so every member lands in one of the four buckets the UI
// surfaces — as a filter value AND as a group-by bucket.
export const INVITED_STATUS_KEYS = ["invited", "expired", "declined"];

// Values are the GraphQL enum names the `presence` where-input takes, so the
// pick travels to the query untranslated. Three states, matching the dot on
// every member avatar: a filter that disagreed with the dots would be wrong
// on its face.
export const PRESENCE_OPTIONS: { value: PresenceStatus; label: string }[] =
  PRESENCE_ORDER.map((value) => ({ value, label: PRESENCE_META[value].label }));

/** The filter keys the chips write. Shared with the members table's group-by
 *  config, whose `filterKey`s must name the same keys. */
export const MEMBER_FILTER_KEYS = {
  status: "memberStatus",
  presence: "presence",
  teams: "teamIds",
  roles: "roleIds",
  manager: "managerId",
} as const;

/** The "no value picked" radio, since a radio group needs a value to hold. */
const ALL_VALUE = "__all__";

// ---------------------------------------------------------------------------
// Filter defs
// ---------------------------------------------------------------------------

/**
 * The quick chips' filter defs. `teams` omitted (not merely empty) drops the
 * Team facet altogether — the team roster is already one team.
 */
export function buildMemberQuickFilterDefs(
  teams: TeamOption[] | undefined,
  roles: RoleRow[],
  teamBadge?: EntityBadgeConfig,
): AppFilterDef<EntityItemConfig>[] {
  const defs: AppFilterDef<EntityItemConfig>[] = [
    {
      key: MEMBER_FILTER_KEYS.status,
      label: "Status",
      icon: "circle",
      type: "select",
      options: MEMBER_STATUS_OPTIONS,
      multiple: false,
      toWhere: ([value]) =>
        value === "invited"
          ? { memberStatus: { in: INVITED_STATUS_KEYS } }
          : { memberStatus: { eq: value } },
    },
    {
      key: MEMBER_FILTER_KEYS.presence,
      label: "Presence",
      icon: "circle",
      type: "select",
      options: PRESENCE_OPTIONS,
      multiple: false,
      toWhere: (values) => ({ presence: values }),
    },
  ];
  if (teams) {
    defs.push({
      key: MEMBER_FILTER_KEYS.teams,
      label: "Team",
      icon: "users",
      type: "entity",
      // `teamBadge` (useTeamOpenBadge, passed by the component caller — this
      // is a plain function) puts the hover "Team" row action on the filter
      // popover's rows, like every other pool listing teams.
      pools: [
        {
          id: TEAM_POOL_ID,
          label: "Teams",
          items: teams.map(teamToPoolItem),
          itemBadge: teamBadge,
        },
      ],
      placeholder: "Search teams...",
      filterFormat: "idArray",
    });
  }
  defs.push(
    {
      key: MEMBER_FILTER_KEYS.roles,
      label: "Role",
      icon: "shield",
      type: "select",
      options: roles.map((r) => ({ value: r.roleId, label: r.roleName })),
    },
    {
      key: MEMBER_FILTER_KEYS.manager,
      label: "Reports to",
      icon: "user",
      type: "entity",
      // The chip builds its pool from workspace data; the def only names the
      // key and the id-array shape the where takes.
      pools: [],
      filterFormat: "idArray",
    },
  );
  return defs;
}

// ---------------------------------------------------------------------------
// Option counts
// ---------------------------------------------------------------------------

/** The where a chip counts within: the view's scope plus every OTHER chip's
 *  filter, so the numbers say what each of ITS options would yield. */
function useCountWhere(
  filters: UseTableFiltersResult<EntityItemConfig>,
  scopeWhere: Record<string, unknown> | undefined,
  ownKeys: readonly string[],
): Record<string, unknown> | undefined {
  const { getWhereExcluding } = filters;
  return useMemo(() => {
    const others = getWhereExcluding(ownKeys);
    return combineWhereConditions(
      [scopeWhere, others].filter((w): w is Record<string, unknown> => w != null),
    );
  }, [getWhereExcluding, scopeWhere, ownKeys]);
}

/** ONE rule for every list: a count shows only when somebody is under the
 *  option. A bare row says "nobody", and the option stays so the vocabulary
 *  reads whole — in a pool of every member a column of zeros would drown the
 *  few numbers that mean anything, and the short menus follow the same rule
 *  so the toolbar's lists agree with each other. */
function shownCount(count: number | undefined): number | undefined {
  return count ? count : undefined;
}

/** A count in the quiet caption ink a picker row's `rowMeta` slot takes. */
function OptionCount({ count }: { count: number }) {
  return (
    <Text size="xs" tabular variant="muted">
      {count}
    </Text>
  );
}

// ---------------------------------------------------------------------------
// Chips
// ---------------------------------------------------------------------------

type ChipsProps = Omittable<{
  filters: UseTableFiltersResult<EntityItemConfig>;
  /** Omit to drop the Team chip (a roster that is already one team). */
  teams?: TeamOption[];
  roles: RoleRow[];
  /** The view's own predicate — a team's membership, the search — that the
   *  option counts are taken within. Omit for a view over every member. */
  scopeWhere?: Record<string, unknown>;
  /** The host list, for the `member.apply_filter` event. */
  via: string;
}>;

export function MemberQuickFilters({ filters, teams, roles, scopeWhere, via }: ChipsProps) {
  const isMobile = useIsMobile();
  const { filterValues, setFilterValues } = filters;
  const pick = useCallback(
    (key: string, value: string | null) => {
      trackEvent({
        context: AnalyticsContext.Member,
        event: MemberEvent.ApplyFilter,
        data: { facet: key, cleared: value == null, via },
      });
      setFilterValues(key, value == null ? [] : [value]);
    },
    [setFilterValues, via],
  );
  const statusCountWhere = useCountWhere(filters, scopeWhere, STATUS_CHIP_KEYS);
  const teamCountWhere = useCountWhere(filters, scopeWhere, TEAM_CHIP_KEYS);
  const roleCountWhere = useCountWhere(filters, scopeWhere, ROLE_CHIP_KEYS);
  const managerCountWhere = useCountWhere(filters, scopeWhere, MANAGER_CHIP_KEYS);

  if (isMobile) {
    return (
      <MemberFacetSheet
        filters={filters}
        teams={teams}
        roles={roles}
        pick={pick}
        statusCountWhere={statusCountWhere}
        teamCountWhere={teamCountWhere}
        roleCountWhere={roleCountWhere}
        managerCountWhere={managerCountWhere}
      />
    );
  }

  return (
    <>
      <StatusChip
        status={filterValues[MEMBER_FILTER_KEYS.status]?.[0] ?? null}
        presence={filterValues[MEMBER_FILTER_KEYS.presence]?.[0] ?? null}
        onPick={pick}
        countWhere={statusCountWhere}
      />
      {teams ? (
        <TeamChip
          teams={teams}
          value={filterValues[MEMBER_FILTER_KEYS.teams]?.[0] ?? null}
          onChange={(id) => pick(MEMBER_FILTER_KEYS.teams, id)}
          countWhere={teamCountWhere}
        />
      ) : null}
      <RoleChip
        roles={roles}
        value={filterValues[MEMBER_FILTER_KEYS.roles]?.[0] ?? null}
        onChange={(id) => pick(MEMBER_FILTER_KEYS.roles, id)}
        countWhere={roleCountWhere}
      />
      <ReportsToChip
        value={filterValues[MEMBER_FILTER_KEYS.manager]?.[0] ?? null}
        onChange={(id) => pick(MEMBER_FILTER_KEYS.manager, id)}
        countWhere={managerCountWhere}
      />
    </>
  );
}

type MemberGroupChipProps = Omit<
  ComponentProps<typeof FilterValueChip>,
  "label" | "size"
>;

/**
 * The Group chip's face, for the group-by dropdown's `trigger`: the labeled
 * `Group: Value ⌄` chip on desktop, the bare layers glyph on a phone —
 * tinted while `selected`, at the one footprint every phone chip holds. The
 * host decides what "set" means (the roster lights on any grouping; the
 * directory on any deviation from its default), so `selected` is its call.
 */
export function MemberGroupChip({
  value,
  selected,
  onClear,
  clearLabel,
  ...props
}: MemberGroupChipProps) {
  const isMobile = useIsMobile();
  if (isMobile) {
    return (
      <FilterToggle
        size="xs"
        icon="layers"
        flag
        label="Group"
        selected={selected}
        {...props}
      />
    );
  }
  return (
    <FilterValueChip
      label="Group"
      value={value}
      selected={selected}
      onClear={onClear}
      clearLabel={clearLabel}
      {...props}
    />
  );
}

// Each chip's own filter keys — left out of the where its counts run over.
const STATUS_CHIP_KEYS = [MEMBER_FILTER_KEYS.status, MEMBER_FILTER_KEYS.presence] as const;
const TEAM_CHIP_KEYS = [MEMBER_FILTER_KEYS.teams] as const;
const ROLE_CHIP_KEYS = [MEMBER_FILTER_KEYS.roles] as const;
const MANAGER_CHIP_KEYS = [MEMBER_FILTER_KEYS.manager] as const;

const STATUS_FACETS: readonly UserFacet[] = ["MEMBER_STATUS", "PRESENCE"];
const TEAM_FACETS: readonly UserFacet[] = ["TEAM"];
const ROLE_FACETS: readonly UserFacet[] = ["ROLE"];
const MANAGER_FACETS: readonly UserFacet[] = ["MANAGER"];

/** The vertical hairline between the narrowing chips and the arrangement
 *  chips (Sort, Group) that follow them. */
export function MemberQuickFiltersDivider() {
  return <Separator orientation="vertical" className="h-4" />;
}

// --- Status: membership + presence, one single-select list ---------------

/** "Invited" folds three server statuses, so its count is their sum. */
function membershipCount(
  counts: ReturnType<typeof useMemberFacetCounts>,
  value: string,
): number | undefined {
  const keys = value === "invited" ? INVITED_STATUS_KEYS : [value];
  let sum = 0;
  for (const key of keys) {
    const n = counts.count("MEMBER_STATUS", key);
    if (n === undefined) return undefined;
    sum += n;
  }
  return sum;
}

function StatusChip({
  status,
  presence,
  onPick,
  countWhere,
}: {
  status: string | null;
  presence: string | null;
  onPick: (key: string, value: string | null) => void;
  countWhere: Record<string, unknown> | undefined;
}) {
  const counts = useMemberFacetCounts(countWhere, STATUS_FACETS);
  const statusCount = (value: string) => membershipCount(counts, value);
  const current = status ?? presence;
  const label =
    MEMBER_STATUS_OPTIONS.find((o) => o.value === current)?.label ??
    PRESENCE_OPTIONS.find((o) => o.value === current)?.label;

  const clear = () => {
    onPick(MEMBER_FILTER_KEYS.status, null);
    onPick(MEMBER_FILTER_KEYS.presence, null);
  };

  return (
    <Menu>
      <MenuTrigger
        render={
          <FilterValueChip
            label="Status"
            value={label}
            onClear={clear}
            clearLabel="Clear status"
          />
        }
      />
      <MenuPopup align="start" width="select">
        <MenuRadioGroup
          value={current ?? ALL_VALUE}
          onValueChange={(value) => {
            const next = String(value);
            if (next === ALL_VALUE) {
              clear();
            } else if (MEMBER_STATUS_OPTIONS.some((o) => o.value === next)) {
              onPick(MEMBER_FILTER_KEYS.status, next);
              onPick(MEMBER_FILTER_KEYS.presence, null);
            } else {
              onPick(MEMBER_FILTER_KEYS.presence, next);
              onPick(MEMBER_FILTER_KEYS.status, null);
            }
          }}
        >
          <MenuRadioItem value={ALL_VALUE} meta={shownCount(counts.total)}>
            All
          </MenuRadioItem>
          <MenuSeparator />
          {MEMBER_STATUS_OPTIONS.map((o) => (
            <MenuRadioItem
              key={o.value}
              value={o.value}
              meta={shownCount(statusCount(o.value))}
            >
              {o.label}
            </MenuRadioItem>
          ))}
          <MenuSeparator />
          {PRESENCE_OPTIONS.map((o) => (
            <MenuRadioItem
              key={o.value}
              value={o.value}
              meta={shownCount(counts.count("PRESENCE", o.value))}
            >
              {o.label}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      </MenuPopup>
    </Menu>
  );
}

// --- Role: the workspace roles ---------------------------------------------

function RoleChip({
  roles,
  value,
  onChange,
  countWhere,
}: {
  roles: RoleRow[];
  value: string | null;
  onChange: (id: string | null) => void;
  countWhere: Record<string, unknown> | undefined;
}) {
  const counts = useMemberFacetCounts(countWhere, ROLE_FACETS);
  // A role that has left the list (deleted, still loading) keeps the chip
  // honest about being set, but has no label to show.
  const label = value ? roles.find((r) => r.roleId === value)?.roleName ?? "Role" : undefined;
  return (
    <Menu>
      <MenuTrigger
        render={
          <FilterValueChip
            label="Role"
            value={label}
            onClear={() => onChange(null)}
            clearLabel="Clear role"
          />
        }
      />
      <MenuPopup align="start" width="select">
        <MenuRadioGroup
          value={value ?? ALL_VALUE}
          onValueChange={(next) =>
            onChange(next === ALL_VALUE ? null : String(next))
          }
        >
          <MenuRadioItem value={ALL_VALUE} meta={shownCount(counts.total)}>
            All
          </MenuRadioItem>
          <MenuSeparator />
          {roles.map((r) => (
            <MenuRadioItem
              key={r.roleId}
              value={r.roleId}
              meta={shownCount(counts.count("ROLE", r.roleId))}
            >
              {r.roleName}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      </MenuPopup>
    </Menu>
  );
}

// --- Entity chips: one pick over a searchable pool -------------------------

/** A single-select entity picker behind a labeled chip. The picked entity's
 *  visual alone is the chip's value: the avatar names a member or team faster
 *  than a truncated label would. Each row's count of members rides its
 *  `rowMeta`, under the one rule every list here follows (`shownCount`).
 *  Exported for the other directory chip bands (the All Teams view) — a
 *  surface without facet counts omits `countOf`. */
type EntityPickOptions = {
  /** The list to pick from. A windowed pool (`useUserPool`'s) brings its
   *  paging, server search and by-id lookup, so the list finds — and the chip
   *  shows — a member past its first page; a plain one is searched in place.
   *  Its `itemBadge` is the hover row action (open profile / open team). */
  pool: EntityPoolConfig;
  searchPlaceholder: string;
  emptyMessage: string;
  value: string | null;
  onChange: (id: string | null) => void;
  countOf?: (id: string) => number | undefined;
};

/** The single-pick selector state behind an entity chip — shared by the chip
 *  and the phone sheet's page, which renders the same list inline. */
function useEntityPick({
  pool,
  searchPlaceholder,
  emptyMessage,
  value,
  onChange,
  countOf,
}: EntityPickOptions) {
  const countedPool = useMemo<EntityPoolConfig>(
    () =>
      countOf
        ? {
            ...pool,
            items: pool.items.map((item) => {
              const count = shownCount(countOf(item.id));
              return count
                ? { ...item, rowMeta: () => <OptionCount count={count} /> }
                : item;
            }),
          }
        : pool,
    [pool, countOf],
  );
  const config = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [countedPool],
      maxSelections: 1,
      searchPlaceholder,
      emptyMessage,
    }),
    [countedPool, searchPlaceholder, emptyMessage],
  );

  // An id whose entity has left the pool still counts as a pick — the query
  // narrows on it — so the chip stays set with its label alone.
  const pools = useMemo(() => [pool], [pool]);
  const ids = useMemo(() => (value ? [value] : []), [value]);
  const { selections } = usePoolSelection(pools, ids);

  // One pick replaces the last, so nothing unresolved is carried (`toIds`).
  const handleChange = useCallback(
    (next: EntitySelection[]) => onChange(next[0]?.id ?? null),
    [onChange],
  );

  return { config, selections, handleChange };
}

export function EntityPickChip({
  label,
  clearLabel,
  renderValue,
  ...options
}: EntityPickOptions & {
  label: string;
  clearLabel: string;
  renderValue: (id: string) => ReactNode;
}) {
  const { config, selections, handleChange } = useEntityPick(options);
  const { pool, emptyMessage, value, onChange } = options;

  return (
    <EntitySelector config={config} value={selections} onValueChange={handleChange} title={label}>
      <EntitySelectorTrigger
        render={
          <FilterValueChip
            label={label}
            value={value ? renderValue(value) : undefined}
            onClear={() => onChange(null)}
            clearLabel={clearLabel}
          />
        }
      />
      <EntitySelectorContent>
        <EntitySelectorInput />
        <EntitySelectorPool value={pool.id} />
        <EntitySelectorEmpty>{emptyMessage}</EntitySelectorEmpty>
      </EntitySelectorContent>
    </EntitySelector>
  );
}

// The chip value is the toolbar's 16px glyph box (the me-mode chip's rung),
// so the tile or avatar sits on the row's icon line instead of reading as a
// larger blob.
// Exported with EntityPickChip: every directory chip renders its picked
// entity's visual on this one rung.
export const VALUE_VISUAL_SIZE = "2xs";

/** A picked member's chip value: their avatar on the chip rung, or a bare
 *  "Member" while the directory has not resolved them. */
export function useMemberChipValue() {
  const resolveMember = useMemberEntityResolver();
  return useCallback(
    (id: string) => {
      const entity = resolveMember(id);
      if (!entity) return <Box as="span">Member</Box>;
      return (
        <EntityTag
          entity={entity}
          display="visual"
          size={VALUE_VISUAL_SIZE}
          interactive={false}
        />
      );
    },
    [resolveMember],
  );
}

function TeamChip({
  teams,
  value,
  onChange,
  countWhere,
}: {
  teams: TeamOption[];
  value: string | null;
  onChange: (id: string | null) => void;
  countWhere: Record<string, unknown> | undefined;
}) {
  const itemBadge = useTeamOpenBadge();
  const pool = useMemo<EntityPoolConfig>(
    () => ({ id: TEAM_POOL_ID, label: "Teams", items: teams.map(teamToPoolItem), itemBadge }),
    [teams, itemBadge],
  );
  const counts = useMemberFacetCounts(countWhere, TEAM_FACETS);
  const countOf = useCallback((id: string) => counts.count("TEAM", id), [counts]);
  const renderValue = useCallback(
    (id: string) => {
      const team = teams.find((t) => t.teamId === id);
      if (!team) return <Box as="span">Team</Box>;
      return (
        <EntityTag
          entity={{ kind: "team", id, team }}
          display="visual"
          size={VALUE_VISUAL_SIZE}
          interactive={false}
        />
      );
    },
    [teams],
  );
  return (
    <EntityPickChip
      label="Team"
      clearLabel="Clear team"
      pool={pool}
      searchPlaceholder="Search teams..."
      emptyMessage="No teams found"
      value={value}
      onChange={onChange}
      renderValue={renderValue}
      countOf={countOf}
    />
  );
}

/** The Reports-to facet's member list: the shared windowed pool (paging and
 *  server search, active and invited members — suspended members can't hold
 *  reports), each row counting who reports to it. One source for the chip
 *  and the phone sheet's page. */
function useReportsToPick(
  value: string | null,
  onChange: (id: string | null) => void,
  countWhere: Record<string, unknown> | undefined,
): EntityPickOptions {
  const { pool } = useUserPool();
  const counts = useMemberFacetCounts(countWhere, MANAGER_FACETS);
  const countOf = useCallback((id: string) => counts.count("MANAGER", id), [counts]);
  return {
    pool,
    searchPlaceholder: "Search members...",
    emptyMessage: "No members found",
    value,
    onChange,
    countOf,
  };
}

function ReportsToChip({
  value,
  onChange,
  countWhere,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
  countWhere: Record<string, unknown> | undefined;
}) {
  const pick = useReportsToPick(value, onChange, countWhere);
  const renderValue = useMemberChipValue();
  return (
    <EntityPickChip
      label="Reports to"
      clearLabel="Clear reports to"
      renderValue={renderValue}
      {...pick}
    />
  );
}

/** The phone sheet's Reports-to page: the chip's list, inline. */
function ReportsToSheetPicker({
  value,
  onChange,
  countWhere,
  onPicked,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
  countWhere: Record<string, unknown> | undefined;
  onPicked: () => void;
}) {
  const { config, selections, handleChange } = useEntityPick(
    useReportsToPick(value, onChange, countWhere),
  );
  return (
    <EntitySelector
      variant="inline"
      open
      config={config}
      value={selections}
      onValueChange={(next) => {
        handleChange(next);
        onPicked();
      }}
      onBeforeSelect={(entity) => {
        if (entity.id !== value) return true;
        onChange(null);
        onPicked();
        return false;
      }}
      title="Reports to"
    >
      <EntitySelectorInput />
      <EntitySelectorPool value={USER_POOL_ID} />
      <EntitySelectorEmpty>No members found</EntitySelectorEmpty>
    </EntitySelector>
  );
}

// ---------------------------------------------------------------------------
// Phone: the facets as a sheet
// ---------------------------------------------------------------------------

type SheetProps = Omittable<{
  filters: UseTableFiltersResult<EntityItemConfig>;
  teams?: TeamOption[];
  roles: RoleRow[];
  pick: (key: string, value: string | null) => void;
  statusCountWhere: Record<string, unknown> | undefined;
  teamCountWhere: Record<string, unknown> | undefined;
  roleCountWhere: Record<string, unknown> | undefined;
  managerCountWhere: Record<string, unknown> | undefined;
}>;

/** The same four facets the chips offer, as the data the phone sheet lists:
 *  one row each, the same single-select values with the same counts. */
function MemberFacetSheet({
  filters,
  teams,
  roles,
  pick,
  statusCountWhere,
  teamCountWhere,
  roleCountWhere,
  managerCountWhere,
}: SheetProps) {
  const { filterValues, clearAll } = filters;
  const status = filterValues[MEMBER_FILTER_KEYS.status]?.[0] ?? null;
  const presence = filterValues[MEMBER_FILTER_KEYS.presence]?.[0] ?? null;
  const team = filterValues[MEMBER_FILTER_KEYS.teams]?.[0] ?? null;
  const role = filterValues[MEMBER_FILTER_KEYS.roles]?.[0] ?? null;
  const manager = filterValues[MEMBER_FILTER_KEYS.manager]?.[0] ?? null;

  const statusCounts = useMemberFacetCounts(statusCountWhere, STATUS_FACETS);
  const teamCounts = useMemberFacetCounts(teamCountWhere, TEAM_FACETS, { skip: !teams });
  const roleCounts = useMemberFacetCounts(roleCountWhere, ROLE_FACETS);
  const resolveMember = useMemberEntityResolver();

  const facets = useMemo((): MemberFacet[] => {
    const list: MemberFacet[] = [
      {
        key: "status",
        label: "Status",
        icon: "circle",
        unfiltered: "Any",
        options: [
          ...MEMBER_STATUS_OPTIONS.map(
            (o): MemberFacetOption => ({
              value: o.value,
              label: o.label,
              group: "Membership",
              count: shownCount(membershipCount(statusCounts, o.value)),
            }),
          ),
          ...PRESENCE_OPTIONS.map(
            (o): MemberFacetOption => ({
              value: o.value,
              label: o.label,
              group: "Presence",
              count: shownCount(statusCounts.count("PRESENCE", o.value)),
            }),
          ),
        ],
        value: status ?? presence,
        onChange: (next) => {
          const isStatus = MEMBER_STATUS_OPTIONS.some((o) => o.value === next);
          pick(MEMBER_FILTER_KEYS.status, next != null && isStatus ? next : null);
          pick(MEMBER_FILTER_KEYS.presence, next != null && !isStatus ? next : null);
        },
      },
    ];
    if (teams) {
      list.push({
        key: "team",
        label: "Team",
        icon: "users",
        unfiltered: "Any team",
        options: teams.map(
          (t): MemberFacetOption => ({
            value: t.teamId,
            label: t.teamName,
            entity: { kind: "team", id: t.teamId, team: t },
            count: shownCount(teamCounts.count("TEAM", t.teamId)),
          }),
        ),
        value: team,
        onChange: (next) => pick(MEMBER_FILTER_KEYS.teams, next),
      });
    }
    list.push(
      {
        key: "role",
        label: "Role",
        icon: "shield",
        unfiltered: "Any role",
        options: roles.map(
          (r): MemberFacetOption => ({
            value: r.roleId,
            label: r.roleName,
            count: shownCount(roleCounts.count("ROLE", r.roleId)),
          }),
        ),
        value: role,
        onChange: (next) => pick(MEMBER_FILTER_KEYS.roles, next),
      },
      {
        key: "manager",
        label: "Reports to",
        icon: "user",
        unfiltered: "Anyone",
        // The member list is windowed, so the page is the chip's own picker
        // rather than a flat option list.
        options: [],
        valueEntity: manager ? resolveMember(manager) : null,
        renderPicker: (onPicked) => (
          <ReportsToSheetPicker
            value={manager}
            onChange={(next) => pick(MEMBER_FILTER_KEYS.manager, next)}
            countWhere={managerCountWhere}
            onPicked={onPicked}
          />
        ),
        value: manager,
        onChange: (next) => pick(MEMBER_FILTER_KEYS.manager, next),
      },
    );
    return list;
  }, [
    teams,
    roles,
    resolveMember,
    managerCountWhere,
    status,
    presence,
    team,
    role,
    manager,
    pick,
    statusCounts,
    teamCounts,
    roleCounts,
  ]);

  const activeCount = facets.filter((facet) => facet.value != null).length;

  return (
    <MemberFilterSheet
      facets={facets}
      activeCount={activeCount}
      onClearAll={clearAll}
    />
  );
}

// ---------------------------------------------------------------------------
// Sort
// ---------------------------------------------------------------------------

type MemberSortKey = "default" | "az" | "za" | "lastJoined" | "firstJoined";

// "Default" is the server's own order — the computed display name no single
// column reproduces — so it is the EMPTY sort, not a column pick. The column
// headers write the same `sorting` state, so a header click that lands on
// one of these reads back as that preset.
const MEMBER_SORT_PRESETS: SortPreset<MemberSortKey>[] = [
  { key: "default", label: "Default", sorting: [] },
  { key: "az", label: "A – Z", sorting: [{ id: "name", desc: false }] },
  { key: "za", label: "Z – A", sorting: [{ id: "name", desc: true }] },
  { key: "lastJoined", label: "Last joined", sorting: [{ id: "creationDate", desc: true }] },
  { key: "firstJoined", label: "First joined", sorting: [{ id: "creationDate", desc: false }] },
];

export function MemberSortChip({
  sorting,
  onSortingChange,
  via,
}: Omittable<{
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  /** The host list, for the `member.change_sorting` event. */
  via: string;
}>) {
  const handleSortingChange = useCallback<OnChangeFn<SortingState>>(
    (next) => {
      const value = typeof next === "function" ? next(sorting) : next;
      trackEvent({
        context: AnalyticsContext.Member,
        event: MemberEvent.ChangeSorting,
        data: { field: value[0]?.id ?? "default", desc: value[0]?.desc ?? false, via },
      });
      onSortingChange(value);
    },
    [onSortingChange, sorting, via],
  );
  return (
    <SortPresetChip
      presets={MEMBER_SORT_PRESETS}
      sorting={sorting}
      onSortingChange={handleSortingChange}
    />
  );
}
